/**
 * Client-side session cache for JPD ideas and segments.
 *
 * Prefetched once when the widget initializes so data is ready
 * before the user opens the modal. Stays in memory for the
 * browser session — no localStorage, no hardcoded defaults.
 */

import type { JpdIdea, SegmentOption } from "../types";

let allIdeas: JpdIdea[] | null = null;
let segments: SegmentOption[] | null = null;
let prefetchDone = false;

/**
 * Prefetch ideas and segments in the background.
 * Called once on widget init — non-blocking, fire-and-forget.
 */
export function prefetch(apiUrl: string, jpdProject: string): void {
  if (prefetchDone || !jpdProject || !apiUrl) return;
  prefetchDone = true;

  // Fire both requests in parallel, don't block anything
  fetchAllIdeas(apiUrl, jpdProject).catch(() => {});
  fetchSegments(apiUrl, jpdProject).catch(() => {});
}

/**
 * Get cached ideas, optionally filtered by query and/or segment.
 * If the cache isn't ready yet, falls back to a live API call.
 */
export async function getIdeas(
  apiUrl: string,
  jpdProject: string,
  query: string,
  segment: string | null,
): Promise<JpdIdea[]> {
  // For user-typed queries (and/or segment filtering), always hit the server.
  // The prefetched cache is intentionally small (recent ideas) and is only suitable
  // for fast initial UI readiness, not full-text search.
  if (query || segment) {
    return await fetchIdeas(apiUrl, jpdProject, query, segment);
  }

  // If cache is warm, return cached ideas (no local filtering)
  if (allIdeas !== null) {
    return allIdeas;
  }

  // Cache not ready — fetch live (this also populates the cache)
  await fetchAllIdeas(apiUrl, jpdProject);
  return allIdeas || [];
}

/**
 * Get cached segments. If not ready, fetches live.
 */
export async function getSegments(apiUrl: string, jpdProject: string): Promise<SegmentOption[]> {
  if (segments !== null) return segments;
  await fetchSegments(apiUrl, jpdProject);
  return segments || [];
}

/** Reset cache (e.g. on widget destroy). */
export function clearCache(): void {
  allIdeas = null;
  segments = null;
  prefetchDone = false;
}

// ── Internal ──

async function fetchAllIdeas(apiUrl: string, jpdProject: string): Promise<void> {
  try {
    const url = `${apiUrl}/api/jpd-ideas?project=${encodeURIComponent(jpdProject)}&q=`;
    const res = await fetch(url, { credentials: "omit" });
    if (!res.ok) return;
    const data = await res.json();
    allIdeas = Array.isArray(data?.ideas) ? data.ideas : [];
  } catch {
    // Prefetch failure is non-fatal — modal will retry live
  }
}

async function fetchIdeas(
  apiUrl: string,
  jpdProject: string,
  query: string,
  segment: string | null,
): Promise<JpdIdea[]> {
  try {
    let url = `${apiUrl}/api/jpd-ideas?project=${encodeURIComponent(jpdProject)}&q=${encodeURIComponent(query)}`;
    if (segment) url += `&segment=${encodeURIComponent(segment)}`;
    const res = await fetch(url, { credentials: "omit" });
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data?.ideas) ? data.ideas : [];
  } catch {
    return [];
  }
}

async function fetchSegments(apiUrl: string, jpdProject: string): Promise<void> {
  try {
    const url = `${apiUrl}/api/jpd-segments?project=${encodeURIComponent(jpdProject)}`;
    const res = await fetch(url, { credentials: "omit" });
    if (!res.ok) return;
    const data = await res.json();
    segments = Array.isArray(data?.segments) ? data.segments : [];
  } catch {
    // Non-fatal
  }
}
